import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../lib/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);

  const fetchShops = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const res = await axios.get(`${API_URL}/api/shops/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data || [];
      setShops(list);
      // Restore selected shop from localStorage or pick first approved
      const savedId = localStorage.getItem('selectedShopId');
      const approved = list.filter(s => s.status === 'approved');
      if (savedId) {
        const found = approved.find(s => s.id === savedId);
        if (found) { setSelectedShop(found); return; }
      }
      if (approved.length > 0) {
        setSelectedShop(approved[0]);
        localStorage.setItem('selectedShopId', approved[0].id);
      } else {
        setSelectedShop(null);
        localStorage.removeItem('selectedShopId');
      }
    } catch {
      setShops([]);
    }
  }, []);

  const selectShop = (shop) => {
    setSelectedShop(shop);
    if (shop) {
      localStorage.setItem('selectedShopId', shop.id);
    } else {
      localStorage.removeItem('selectedShopId');
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('access_token');
      
      if (storedUser && storedUser !== 'undefined' && token && token !== 'undefined') {
        try { setUser(JSON.parse(storedUser)); } catch { await logout(); return; }
        try {
          const response = await auth.me();
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch {
          await logout();
        }
      }
    } catch (e) {
      console.log('Error checking auth:', e);
    } finally {
      setLoading(false);
    }
  };

  // Fetch shops when user changes
  useEffect(() => {
    if (user) { fetchShops(); }
    else { setShops([]); setSelectedShop(null); }
  }, [user, fetchShops]);

  const login = async (email, password) => {
    const response = await auth.login({ email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await auth.register(data);
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = async (idToken) => {
    const response = await auth.googleAuth(idToken);
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedShopId');
    setUser(null);
    setShops([]);
    setSelectedShop(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const isAdmin = user?.role === 'ADMIN';
  const isEditor = user?.role === 'EDITOR';
  const isOrg = user?.role === 'ORG';
  const hasShop = shops.filter(s => s.status === 'approved').length > 0;
  const canAccessAdmin = isAdmin || isEditor || isOrg || hasShop;
  
  const token = localStorage.getItem('access_token');

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      loginWithGoogle,
      register,
      logout,
      updateUser,
      isAdmin,
      isEditor,
      isOrg,
      hasShop,
      shops,
      selectedShop,
      selectShop,
      fetchShops,
      canAccessAdmin,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
